import React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from '@/../i18n';
import { map, truncate } from 'lodash-es';
import styled from 'styled-components';
import { ContentLoader, DialogBody, BasicTableRowsProps, TertiaryButton } from '@dev/ee-components';
import { AccessLevel, ContactField, ContactFieldType } from '@dev/ee-api/ee-api-models';

import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { DeleteContactFieldStart, getCustomFieldsStore } from '@/store/customFields/actions';
import { hasAccessLevel } from '@/services/Secuirty';

const fieldDetails = {
    [ContactFieldType.Number]: '–2147483648 to 2147483647',
    [ContactFieldType.DateTime]: 'YYYY-MM-DDThh:mm:ss',
    [ContactFieldType.Currency]: '–79228162514264337593543950335 to 79228162514264337593543950335',
    [ContactFieldType.Boolean]: i18n.t('placeholderboolean'),
    [ContactFieldType.BigNumber]: '–999999999999999999 to 999999999999999999',
    [ContactFieldType.Date]: 'YYYY-MM-DD',
};

const NameContainer = styled.div`
    padding-right: 15px;
`;

const skeletonTemplate = {
    id: `fieldsRow--1`,
    datatype: <ContentLoader mode="row" />,
    fieldname: <ContentLoader mode="row" />,
    details: <ContentLoader mode="row" />,
    options: <ContentLoader mode="row" />,
};

const skeletonRows = map([...new Array(5)], (): BasicTableRowsProps => skeletonTemplate);

export const useTableRows = (visibleFields): BasicTableRowsProps[] => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isLoadingFields } = useSelector(getCustomFieldsStore);
    const canModifyCustomFields = hasAccessLevel(AccessLevel.ModifyCustomFields);

    const handleDeleteField = (fieldName: string): void => {
        const params: ShowDialogParams = {
            title: t('removecustomfield'),
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => {
                dispatch(DeleteContactFieldStart(fieldName));
            },
            cancelLabel: t('cancel'),
            body: (
                <DialogBody variant={'alert'} above={`${t('confirmdeletefield')} ${fieldName}?`}>
                    {t('deletefieldalert')}
                </DialogBody>
            ),
        };
        dispatch(ShowDialog(params));
    };

    const rows = !visibleFields
        ? []
        : visibleFields.map(
              (field: ContactField, index): BasicTableRowsProps => ({
                  id: `fieldsRow--${index}`,
                  datatype: field.Type,
                  fieldname: <NameContainer>{truncate(field.Name, { length: 45 })}</NameContainer>,
                  details: field.Type === 'Text' ? field.Length : fieldDetails[field.Type],
                  options: canModifyCustomFields && (
                      <TertiaryButton
                          id={`delete-button-row-${index}`}
                          className="deleteButton"
                          text={t('delete')}
                          size="small"
                          onClick={(): void => {
                              handleDeleteField(field.Name);
                          }}
                      />
                  ),
              })
          );

    return isLoadingFields ? skeletonRows : rows;
};
